import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle2, Clock, Download, FileSignature } from 'lucide-react';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { getErrorMessage } from '../../lib/apiClient';
import {
  downloadCompleted,
  getSignerPdf,
  getSignerSession,
  signatureKeys,
  submitAgreement,
} from './signaturesApi';
import PublicShell from './components/PublicShell';
import SigningFlow from './components/SigningFlow';
import { loadPdf } from './lib/pdf';

/**
 * The guest agreement-signing experience at /sign/:token — identity confirm →
 * PDF with live field overlay → thank-you (OtterWaiver's AgreementSignView).
 */
export default function PublicSignerPage() {
  const { token } = useParams();
  const [stage, setStage] = useState('welcome'); // welcome | signing | done
  const [consent, setConsent] = useState(false);

  // Audit moments for the Certificate of Completion. `viewedAt` is the first
  // render of this page; `consentedAt` the first tick of the consent box. Refs,
  // not state: they must never re-render, and must never move once recorded.
  const viewedAt = useRef(new Date().toISOString());
  const consentedAt = useRef(null);
  const onConsentChange = (checked) => {
    if (checked && !consentedAt.current) consentedAt.current = new Date().toISOString();
    setConsent(checked);
  };
  const [pdf, setPdf] = useState(null);
  const [result, setResult] = useState(null);

  const session = useQuery({
    queryKey: signatureKeys.signerSession(token),
    queryFn: () => getSignerSession(token),
    retry: false,
  });

  const pdfBytes = useQuery({
    queryKey: signatureKeys.signerPdf(token),
    queryFn: () => getSignerPdf(token),
    enabled: stage === 'signing',
    staleTime: Infinity,
  });

  useEffect(() => {
    let cancelled = false;
    if (pdfBytes.data) {
      loadPdf(pdfBytes.data).then((loaded) => {
        if (!cancelled) setPdf(loaded);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [pdfBytes.data]);

  const submit = useMutation({
    mutationFn: ({ values, audit }) =>
      submitAgreement({
        token,
        consent: true,
        values,
        audit: {
          ...audit,
          viewedAt: viewedAt.current,
          consentedAt: consentedAt.current ?? undefined,
        },
      }),
    onSuccess: (data) => {
      setResult(data);
      setStage('done');
      window.scrollTo({ top: 0 });
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  if (session.isLoading) {
    return (
      <PublicShell>
        <div className="flex h-64 items-center justify-center">
          <Spinner />
        </div>
      </PublicShell>
    );
  }

  if (session.error) {
    return (
      <PublicShell title="Document unavailable">
        <StatusCard
          icon={Clock}
          title="This signing link isn't available"
          body={getErrorMessage(session.error, 'The link may be invalid, expired, or withdrawn.')}
        />
      </PublicShell>
    );
  }

  const { document: doc, signer, fields, progress } = session.data;
  const signerName = `${signer.firstName} ${signer.lastName ?? ''}`.trim();

  // Already handled: show status instead of the signing surface.
  if (stage !== 'done' && signer.isSigned) {
    const completed = doc.status === 'completed';
    return (
      <PublicShell title={doc.title}>
        <StatusCard
          icon={completed ? CheckCircle2 : Clock}
          title={completed ? 'This document is complete' : 'You have already signed'}
          body={
            completed
              ? 'All parties have signed. You can download the final document below.'
              : `Waiting on ${progress.total - progress.signed} other signer${
                  progress.total - progress.signed === 1 ? '' : 's'
                }. You'll receive the final copy by email once everyone has signed.`
          }
        >
          {completed && (
            <Button
              onClick={() =>
                downloadCompleted(token, doc.title).catch((err) =>
                  toast.error(getErrorMessage(err)),
                )
              }
            >
              <Download className="h-4 w-4" /> Download signed PDF
            </Button>
          )}
        </StatusCard>
      </PublicShell>
    );
  }

  if (stage === 'done') {
    return (
      <PublicShell title={doc.title}>
        <StatusCard
          icon={CheckCircle2}
          title="Thank you for signing!"
          body={
            result?.completed
              ? 'All parties have now signed — the final document is ready.'
              : "We've recorded your signature. You'll receive the final copy by email once everyone has signed."
          }
        >
          {result?.completed && result?.downloadToken && (
            <Button
              onClick={() =>
                downloadCompleted(result.downloadToken, doc.title).catch((err) =>
                  toast.error(getErrorMessage(err)),
                )
              }
            >
              <Download className="h-4 w-4" /> Download signed PDF
            </Button>
          )}
        </StatusCard>
      </PublicShell>
    );
  }

  if (stage === 'welcome') {
    return (
      <PublicShell title={doc.title} subtitle={`For ${signerName}`}>
        <div className="mx-auto max-w-lg space-y-5 rounded-2xl border border-slate-200 bg-slate-100 p-8">
          <FileSignature className="h-10 w-10 text-brand-600" />
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              {signerName}, you&apos;ve been asked to sign
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              &ldquo;{doc.title}&rdquo; · {doc.pagesInPdf} page{doc.pagesInPdf === 1 ? '' : 's'} ·
              sent to {signer.email}
            </p>
          </div>
          <label className="flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => onConsentChange(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-brand-600"
            />
            I agree to use electronic records and signatures, and I confirm I am the person named
            above.
          </label>
          <Button className="w-full" disabled={!consent} onClick={() => setStage('signing')}>
            Review & sign
          </Button>
        </div>
      </PublicShell>
    );
  }

  // signing
  return (
    <PublicShell title={doc.title} subtitle={`Signing as ${signerName} · ${signer.email}`}>
      {!pdf ? (
        <div className="flex h-64 items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <SigningFlow
          pdf={pdf}
          fields={fields}
          signer={signer}
          submitting={submit.isPending}
          onSubmit={(values, audit) => submit.mutate({ values, audit })}
        />
      )}
    </PublicShell>
  );
}

function StatusCard({ icon: Icon, title, body, children }) {
  return (
    <div className="mx-auto max-w-lg space-y-4 rounded-2xl border border-slate-200 bg-slate-100 p-8 text-center">
      <Icon className="mx-auto h-12 w-12 text-brand-600" />
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      <p className="text-sm leading-relaxed text-slate-500">{body}</p>
      {children}
    </div>
  );
}
